import '../css/app.css';

import { createInertiaApp, router } from '@inertiajs/vue3';
import { browserTracingIntegration, captureException, init as initSentry } from '@sentry/vue';
import axios from 'axios';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import type { DefineComponent } from 'vue';
import { createApp, h } from 'vue';
import { ZiggyVue } from 'ziggy-js';
import { initializeFreshdeskWidget, syncFreshdeskWidget } from './utils/freshdeskWidget';
import { registerStoryblok } from './storyblok/registerStoryblok';
import { isUnactionableCallStackOverflow } from './utils/isUnactionableCallStackOverflow.mjs';
import { sanitizeHistoryState } from './utils/sanitizeHistoryState.mjs';
import { pushPageView } from './utils/trackingEventHelpers';
import type { SharedData } from './types';

function applyZiggyFromPage(page: { props: { ziggy?: SharedData['ziggy'] } }): void {
    const next = page.props.ziggy;

    if (!next) {
        return;
    }

    // @routes' `const Ziggy` is what route() reads. app.blade.php aliases
    // window.Ziggy to that object — mutate it so client-side navigations pick
    // up path-scoped groups (e.g. business.* after /frankfurt → /business/login).
    const current = window.Ziggy;

    if (current) {
        current.url = next.url;
        current.port = next.port;
        current.defaults = next.defaults;
        current.routes = next.routes;
        return;
    }

    window.Ziggy = next;
}

// When the browser restores a page from the bfcache (back/forward cache),
// any element that was in :active state keeps its pressed appearance.
// Blurring the active element on pageshow clears that stale visual state.
window.addEventListener('pageshow', (event) => {
    if (event.persisted && document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
    }
});

// Inertia 2.0 writes the Vue-reactive page object to history.state. iOS JSC
// then overflows while structured-cloning those proxies (GLOWCARD-6M).
if (typeof window !== 'undefined') {
    const originalReplaceState = window.history.replaceState.bind(window.history);
    const originalPushState = window.history.pushState.bind(window.history);

    window.history.replaceState = (data, unused, url) => {
        originalReplaceState(sanitizeHistoryState(data), unused, url);
    };
    window.history.pushState = (data, unused, url) => {
        originalPushState(sanitizeHistoryState(data), unused, url);
    };
}

// Inertia reads window.history.state.scrollRegions on init / back-forward.
// Safari (and some Android browsers) can leave history.state as null, which throws
// before optional chaining inside @inertiajs/core — normalize before Inertia boots.
if (typeof window !== 'undefined' && window.history.state == null) {
    window.history.replaceState(
        { scrollRegions: [], documentScrollPosition: { top: 0, left: 0 } },
        '',
        window.location.href,
    );
}

// Configure Axios to include CSRF token
axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';
const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
if (csrfToken) {
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
}

// Configure Axios interceptor for error tracking
axios.interceptors.response.use(
    (response) => response,
    (error) => {
        const status = error.response?.status;

        // 409 = Inertia asset version mismatch (stale frontend after deploy) — handled by Inertia internally
        // 419 = Laravel CSRF token expiry — expected for long-lived sessions
        const ignoredStatuses = [409, 419];

        // Cloudflare bot-protection challenges return 403 with their own HTML page.
        // These are external to our application and not actionable on our side.
        const isCloudflareChallenged =
            status === 403 &&
            typeof error.response?.data === 'string' &&
            error.response.data.includes('Just a moment...');

        if (import.meta.env.VITE_SENTRY_DSN && error.response && !ignoredStatuses.includes(status) && !isCloudflareChallenged) {
            captureException(error, {
                extra: {
                    url: error.config?.url,
                    method: error.config?.method,
                    status,
                    statusText: error.response?.statusText,
                    data: error.response?.data,
                },
                tags: {
                    'http.status_code': status,
                    'http.method': error.config?.method,
                },
            });
        }
        return Promise.reject(error);
    },
);

// The initial page_view is pushed synchronously in setup() (before app.mount) so it
// always precedes any onMounted ecommerce event. Inertia v2 ALSO fires a navigate
// event for the initial page load, but asynchronously (after await boundaries in
// router.init), which drains microtasks first and causes ecommerce events to precede
// page_view. We therefore push in setup() and skip the first navigate event to avoid
// a duplicate page_view on initial load / reload.
let firstNavigateSkipped = false;

router.on('navigate', (event) => {
    // Path-scoped Ziggy groups (business.*, …) are excluded from the public @routes
    // dump. Inertia shares the merged list on each visit, but window.Ziggy is only
    // written on full page loads — sync it so client-side navigation from a public
    // page (e.g. /frankfurt → /business/login) can resolve those names.
    applyZiggyFromPage(event.detail.page);
    syncFreshdeskWidget(event.detail.page);

    if (!firstNavigateSkipped) {
        firstNavigateSkipped = true;
        return;
    }
    pushPageView(event.detail.page.url);
});

const appName = import.meta.env.VITE_APP_NAME || 'Laravel';

// Many pages already brand their own <Head title> (e.g. "... – GLOWCARD"), so only
// append the app name when a page's title doesn't already mention it — avoids
// "Page – GLOWCARD - GLOWCARD". Must match resources/js/ssr.ts's callback exactly,
// otherwise the title set server-side via @inertiaHead flashes on hydration.
const formatTitle = (title: string) => (title.includes(appName) ? title : `${title} - ${appName}`);

createInertiaApp({
    title: formatTitle,
    resolve: (name) => resolvePageComponent(`./pages/${name}.vue`, import.meta.glob<DefineComponent>('./pages/**/*.vue')),
    setup({ el, App, props, plugin }) {
        // Push page_view synchronously BEFORE app.mount() so it always lands in the
        // dataLayer before any onMounted ecommerce event (queueMicrotask).
        pushPageView(window.location.href);
        applyZiggyFromPage(props.initialPage);

        const app = createApp({ render: () => h(App, props) });

        // Initialize Sentry for error tracking
        if (import.meta.env.VITE_SENTRY_DSN) {
            initSentry({
                app,
                dsn: import.meta.env.VITE_SENTRY_DSN,
                environment: import.meta.env.VITE_APP_ENV || import.meta.env.MODE, // 'staging' or 'production' or 'development'
                integrations: [
                    browserTracingIntegration(),
                ],
                // Performance Monitoring
                tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0, // 10% in production, 100% in development
                // Track user information
                beforeSend(event, hint) {
                    // Add user context from Laravel's props if available
                    const pageProps = props.initialPage?.props as any;
                    if (pageProps?.auth?.user) {
                        event.user = {
                            id: pageProps.auth.user.id,
                            email: pageProps.auth.user.email,
                            username: pageProps.auth.user.name,
                        };
                    }

                    const error = hint?.originalException;
                    const errorMessage = error instanceof Error ? error.message : String(error ?? '');

                    // iOS JSC / Chrome Translate recurse on the HTML document (GLOWCARD-71).
                    // Keep events that still have a frame from our Vite bundle.
                    if (isUnactionableCallStackOverflow(event, error)) {
                        return null;
                    }

                    // Inertia back/forward: window.history.state can be null in some browsers before
                    // our replaceState polyfill runs (e.g. race) or in edge cases. @inertiajs/core
                    // accesses state.scrollRegions without a null guard.
                    if (
                        errorMessage.includes("Cannot read properties of null (reading 'scrollRegions')") ||
                        errorMessage.includes("Cannot read properties of null (reading 'documentScrollPosition')") ||
                        errorMessage.includes("evaluating 'window.history.state.scrollRegions'") ||
                        errorMessage.includes('evaluating "window.history.state.scrollRegions"') ||
                        errorMessage.includes("evaluating 'window.history.state.documentScrollPosition'")
                    ) {
                        return null;
                    }

                    // Pinterest pixel / conversion script — load failures in strict privacy environments
                    if (errorMessage.includes('ct.pinterest.com')) {
                        return null;
                    }

                    // Third-party Consent Management Platform (Usercentrics) fails to load its
                    // own dynamic chunks in some environments. Not actionable on our side.
                    if (errorMessage.includes('web.cmp.usercentrics.eu')) {
                        return null;
                    }

                    // Pinterest conversion-tracking script fires fetch() calls to ct.pinterest.com
                    // that fail in ad-blocking or privacy-strict environments. The error originates
                    // entirely inside Pinterest's own JS and is not actionable on our side.
                    const frames = event.exception?.values?.[0]?.stacktrace?.frames ?? [];
                    const isPinterestFrame = frames.some((f: { filename?: string }) =>
                        f.filename?.includes('ct.pinterest.com') || f.filename?.includes('pintrk'),
                    );
                    if (isPinterestFrame) {
                        return null;
                    }

                    // Browser extension errors are never actionable on our side. Extensions inject
                    // their own scripts which can fail independently of our application code.
                    // Safari uses webkit-masked-url://hidden/, Chrome uses chrome-extension://, Firefox uses moz-extension://.
                    const isBrowserExtensionFrame = frames.some((f: { filename?: string }) =>
                        f.filename?.startsWith('webkit-masked-url://') ||
                        f.filename?.startsWith('chrome-extension://') ||
                        f.filename?.startsWith('moz-extension://'),
                    );
                    if (isBrowserExtensionFrame) {
                        return null;
                    }

                    // Generic network errors on mobile (offline, flaky connection, etc.) are not
                    // actionable and generate noise. Only suppress bare "Network Error" without a
                    // response status, since those with a status code are already captured by the
                    // Axios interceptor with full context.
                    if (errorMessage === 'Network Error' && !event.tags?.['http.status_code']) {
                        return null;
                    }

                    return event;
                },
            });
        }

        app.use(plugin).use(ZiggyVue);

        const storyblokPreviewQuery =
            typeof window !== 'undefined' && /(?:^|[?&])_storyblok=/.test(window.location.search);
        const enableStoryblokVisualEditorBridge =
            import.meta.env.DEV ||
            import.meta.env.VITE_STORYBLOK_ENABLE_VISUAL_EDITOR === 'true' ||
            storyblokPreviewQuery;

        registerStoryblok(app, { bridge: enableStoryblokVisualEditorBridge });

        app.mount(el);

        initializeFreshdeskWidget(props.initialPage);
    },
    progress: {
        color: '#4B5563',
    },
});
